import {
    Draggable,
    Droppable,
    type DraggableProvided,
    type DroppableProvided,
} from '@hello-pangea/dnd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { aiApi, isAiGenerationCancelled } from '@/api/ai';
import { ArrowDown2, Chart, Edit2, GripVertical, Sparkles, Trash } from '@/components/icons';
import { HoverIntentGroup } from '@/components/ui/hover-intent-group';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
    buildAiBusyReason,
    getGeneratingSectionTitle,
} from '@/components/wizard/ai-generation-lock';
import { SECTIONS } from '@/components/wizard/constants';
import AddInstrumentMenu from '@/components/wizard/test-blocks/AddInstrumentMenu';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import { createTextBlock } from '@/lib/table-utils';
import { useReportWizard } from '@/pages/create-report/context/ReportWizardContext';
import { TableBlock, TestBlock, TextBlock } from '@/types/report-payload';
import { AISectionSkeleton } from './AISectionSkeleton';
import { getBlockRealId } from './block-selection/blockClipboard';
import { resolveGroupMemberIds } from './block-selection/blockGroups';
import { BlockPasteGap, TestDropIndicator } from './block-selection/BlockSelectionChrome';
import { useDropIndicator } from './block-selection/DropIndicatorContext';
import { ScopeBand, type ScopeBandEdges } from './block-selection/ScopeBand';
import { useTestBlockSelection } from './block-selection/TestBlockSelectionContext';
import { BLOCK_TEXT_SPACING_RESET, SHOW_TABLE_BLOCK_RAIL } from './constants';
import EditableText from './EditableText';
import { getFootnoteBlockId } from './footnotes';
import { MeasureWrapper } from './MeasureWrapper';
import { DEFAULT_SECTION_HEIGHT, DEFAULT_TITLE_HEIGHT } from './pagination/constants';
import {
    getBlockMeasurementId,
    isSplitBlockId,
    isSplitContinuationId,
    stripSplitBlockId,
} from './pagination/utils';
import TestChart from './TestChart';
import TestDivider from './TestDivider';
import TestImage from './TestImage';
import TestTable from './TestTable';
import TestText from './TestText';
import { ReportData } from './types';
import type { ReportFootnotePlan } from './footnotes';
import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from 'react';

interface TestSectionRendererProps {
    data: ReportData;
    onUpdate: (updates: Partial<ReportData> | ((prev: ReportData) => Partial<ReportData>)) => void;
    disabled?: boolean;
    /** Without a blockId, opens the instrument selector in create mode. */
    onAddTest: (blockId?: string | number, mode?: 'default' | 'inline') => void;
    /**
     * Only the empty state uses it, for the "Instrumento Livre" half of its
     * menu — the Satepsi half stays on `onAddTest`. Optional so the hidden
     * measurer, which never renders that CTA, needs no no-op for it.
     */
    onAddBlock?: (toolId: string) => void;
    onAddChartFromTable: (blockId: string) => void;
    onEditChart: (block: TestBlock) => void;
    measureBlocks?: boolean;
    enableDragAndDrop?: boolean;
    droppableKey?: string;
    blocksOverride?: TestBlock[];
    blockStartIndex?: number;
    editingBlockId?: string | number | null;
    onEditingBlockChange?: (blockId: string | number | null) => void;
    footnotePlan?: ReportFootnotePlan;
}

export default function TestSectionRenderer({
    data,
    onUpdate,
    onAddTest,
    onAddBlock,
    onAddChartFromTable,
    onEditChart,
    disabled = false,
    measureBlocks = false,
    enableDragAndDrop = true,
    droppableKey,
    blocksOverride,
    blockStartIndex,
    editingBlockId,
    onEditingBlockChange,
    footnotePlan,
}: TestSectionRendererProps) {
    const testsSection = data.tests || { section_title: '', blocks: [] };
    const testBlocks = blocksOverride || testsSection.blocks || [];

    // Fallback for callers that pass blocksOverride without the pagination-computed
    // blockStartIndex: re-derive it by matching the first block back to the global list.
    const findBlockStartIndex = () => {
        if (!blocksOverride || blocksOverride.length === 0) return 0;
        const firstBlock = blocksOverride[0];
        const originalBlocks = testsSection.blocks || [];
        const normalizedFirstBlockId = stripSplitBlockId(firstBlock.id);
        const originalIndex = originalBlocks.findIndex((block) => {
            if (normalizedFirstBlockId && block.id) {
                return stripSplitBlockId(block.id) === normalizedFirstBlockId;
            }
            return block.order === firstBlock.order;
        });
        return originalIndex >= 0 ? originalIndex : 0;
    };

    const resolvedBlockStartIndex = blockStartIndex ?? findBlockStartIndex();

    const blockSelection = useTestBlockSelection();
    const showSelectionChrome = blockSelection.enabled && !disabled && !measureBlocks;
    const dropTarget = useDropIndicator();

    const selectionRingClass = (realId: string) =>
        showSelectionChrome && blockSelection.isSelected(realId)
            ? 'rounded-md bg-blue-50/40 ring-2 ring-blue-400/80 ring-offset-4'
            : '';

    // Cmd/Ctrl+click toggles and Shift+click range-extends the block selection
    // without entering edit mode.
    //
    // A plain click points the tools panel at the block *without consuming the
    // event*: the cell editor, the interpretation editor and the chart modal
    // all still open exactly as before. Scoping is deliberately a side effect
    // rather than a gesture of its own — a modifier key was the only door into
    // the panel's second level, and usability testing showed people never find
    // one. Selection stays a separate, explicit act because it is also what
    // Delete acts on.
    const selectionCaptureProps = (realId: string) => {
        if (!showSelectionChrome) return {};

        const isSelectionModifier = (event: ReactMouseEvent | ReactPointerEvent) =>
            event.metaKey ||
            event.ctrlKey ||
            (event.shiftKey && blockSelection.selectedIds.size > 0);

        return {
            onClickCapture: (event: ReactMouseEvent) => {
                if (!isSelectionModifier(event)) {
                    scopePanelToBlock(realId);
                    return;
                }

                event.preventDefault();
                event.stopPropagation();
                blockSelection.select(realId, event.metaKey || event.ctrlKey ? 'toggle' : 'range');
            },
            // Right-click points the panel at the block too. Opening the
            // context menu is a way of acting on a block, and without this the
            // panel keeps talking about whatever was clicked last while the
            // menu offers actions for something else. Capture phase, because
            // the table editor stops `contextmenu` from bubbling so the
            // page-level menu does not stack on top of the cell menu.
            onContextMenuCapture: () => {
                scopePanelToBlock(realId);
            },
            onMouseDownCapture: (event: ReactMouseEvent) => {
                if (isSelectionModifier(event)) event.stopPropagation();
            },
            onPointerDownCapture: (event: ReactPointerEvent) => {
                if (isSelectionModifier(event)) event.stopPropagation();
            },
        };
    };

    const {
        silentSave,
        isGenerating,
        setIsGenerating,
        beginGeneration,
        endGeneration,
        setGeneratingTestBlockId,
        setGeneratingSectionId,
        generatingSectionId,
        sidebarSteps,
        handleStepClick,
        scopePanelToBlock,
        panelScope,
        spotlightAnchorId,
        pendingBlockAction,
        setPendingBlockAction,
    } = useReportWizard();

    // Which instrument the sidebar list asked us to point at. Resolved over the
    // *whole* section rather than `testBlocks`, which on a paginated report is
    // only this page's slice and would cut the run in half. A group owns no
    // element of its own, so the mark goes on every member block.
    //
    // Gated on `panelScope` on purpose: Escape, the back crumb and leaving the
    // step all null it already, so the mark inherits those three exits for free
    // instead of needing its own effect. Its own exit — a pointerdown anywhere
    // in the preview — lives on the container in `CreateReport`.
    // Kept in document order, not just as a lookup: the band caps only at the
    // run's real ends, so it has to know which member is the first and the last.
    const highlightedMemberIds = useMemo(
        () =>
            showSelectionChrome && panelScope
                ? resolveGroupMemberIds(testsSection.blocks || [], spotlightAnchorId)
                : [],
        [showSelectionChrome, testsSection.blocks, panelScope, spotlightAnchorId],
    );

    const highlightedIds = useMemo(() => new Set(highlightedMemberIds), [highlightedMemberIds]);
    const groupFirstId = highlightedMemberIds[0];
    const groupLastId = highlightedMemberIds[highlightedMemberIds.length - 1];

    /**
     * One member's slice of the instrument band, or null when the block is not
     * part of the marked run. Each slice bleeds into half the gap it shares with
     * its neighbour, so consecutive members meet with no seam and the run reads
     * as one shape rather than one box per block.
     *
     * One step below the selection ring on purpose: selection (ring-2, blue) is
     * what Delete acts on, the band is only what the panel is pointed at.
     */
    const resolveScopeBand = (localIndex: number): ScopeBandEdges | null => {
        const block = testBlocks[localIndex];
        if (!block) return null;

        const realId = getBlockRealId(block);
        if (!highlightedIds.has(realId)) return null;

        const neighbourIsMember = (offset: number) => {
            const neighbour = testBlocks[localIndex + offset];
            return !!neighbour && highlightedIds.has(getBlockRealId(neighbour));
        };

        // A fragment at a chunk edge continues on the neighbouring page — the
        // same test the paste gap and the drop indicator already use. Capping
        // there would close off a band that is not finished.
        const continuesAbove = localIndex === 0 && isSplitContinuationId(block.id);
        const continuesBelow = localIndex === testBlocks.length - 1 && isSplitBlockId(block.id);

        return {
            capTop: !neighbourIsMember(-1) && !continuesAbove && realId === groupFirstId,
            capBottom: !neighbourIsMember(1) && !continuesBelow && realId === groupLastId,
        };
    };

    const hasPatient = Boolean(data.patient?.id);

    const handleChoosePatientFromCanvas = () => {
        const patientStepIndex = sidebarSteps.findIndex((step) => step.id === SECTIONS.PATIENT);
        handleStepClick(Math.max(patientStepIndex, 0));
    };

    /**
     * The Satepsi half deliberately stays on `onAddTest`: with no blocks in the
     * section, "append at the end" is the only position there is, so it and
     * `onAddBlock('tool_test')` are the same call — and keeping it means the
     * path that worked before this menu existed is untouched. The free and
     * pasted instruments need the wider handler.
     */
    const handleAddInstrumentFromCanvas = (toolId: string) => {
        if (toolId === 'tool_test_free' || toolId === 'tool_test_paste') {
            onAddBlock?.(toolId);
            return;
        }

        onAddTest();
    };
    const aiCreditAccess = useFeatureEntitlement('ai.credits');
    const [consolidatingTableId, setConsolidatingTableId] = useState<string | null>(null);
    // Distinct from `consolidatingTableId`, which means "a brand-new
    // interpretation is being written under this table" and drives the skeleton.
    // This one means "this table holds the lock" — it is set for re-runs too,
    // where there is already text and so no skeleton.
    const [generatingTableId, setGeneratingTableId] = useState<string | null>(null);
    const sectionRef = useRef<HTMLDivElement | null>(null);

    const handleBlockUpdate = useCallback(
        (blockId: string | number, updates: Partial<TestBlock>) => {
            onUpdate((prev) => {
                const currentTests = prev.tests || {
                    section_title: '',
                    blocks: [],
                };
                const allBlocks = currentTests.blocks || [];
                const newBlocks = allBlocks.map((b) => {
                    const isIdTarget = typeof blockId === 'string';
                    const normalizedTargetId = isIdTarget ? stripSplitBlockId(blockId) : '';
                    const match = isIdTarget
                        ? stripSplitBlockId(b.id) === normalizedTargetId
                        : !b.id && b.order === blockId;

                    if (!match) return b;

                    if ('automatic' in b && b.automatic) {
                        return { ...b, ...updates, automatic: false };
                    }

                    return { ...b, ...updates };
                }) as TestBlock[];

                return {
                    tests: {
                        ...currentTests,
                        blocks: newBlocks,
                    },
                };
            });
        },
        [onUpdate],
    );

    const handleDeleteBlock = useCallback(
        (blockId: string | number) => {
            onUpdate((prev) => {
                const currentTests = prev.tests || {
                    section_title: '',
                    blocks: [],
                };
                const allBlocks = currentTests.blocks || [];
                const normalizeSplitId = (value?: string | number) => stripSplitBlockId(value);
                const targetId = normalizeSplitId(blockId);
                const isIdTarget = typeof blockId === 'string' && isNaN(Number(blockId));
                const isSplitId = isSplitBlockId(blockId);

                const targetIndex = allBlocks.findIndex((b) => {
                    if (isIdTarget || isSplitId) {
                        return normalizeSplitId(b.id) === targetId;
                    }

                    return !b.id && b.order === blockId;
                });

                if (targetIndex === -1) return {};

                const targetBlock = allBlocks[targetIndex];

                // Deleting a linked text block (table companion) → clear it instead of removing
                const isLinkedTextBlock =
                    targetBlock?.block_type === 'text' &&
                    !!(targetBlock as TextBlock).linked_table_id;

                if (isLinkedTextBlock) {
                    const resetBlocks = allBlocks.map((block, index) => {
                        if (index !== targetIndex || block.block_type !== 'text') {
                            return block;
                        }

                        return {
                            ...block,
                            content: '',
                            automatic: false,
                            has_content: true,
                        };
                    });

                    return {
                        tests: {
                            ...currentTests,
                            blocks: resetBlocks,
                        },
                    };
                }

                // Deleting a table → also remove any linked text/chart blocks
                const linkedTextIndices = new Set<number>();
                const linkedChartIndices = new Set<number>();
                if (targetBlock?.block_type === 'table' && targetBlock.id) {
                    allBlocks.forEach((b, i) => {
                        if (
                            b.block_type === 'text' &&
                            (b as TextBlock).linked_table_id === targetBlock.id
                        ) {
                            linkedTextIndices.add(i);
                        }

                        if (b.block_type === 'chart' && b.linked_table_id === targetBlock.id) {
                            linkedChartIndices.add(i);
                        }
                    });
                }

                const newBlocks = allBlocks.filter((_, index) => {
                    if (index === targetIndex) return false;
                    if (linkedTextIndices.has(index)) return false;
                    if (linkedChartIndices.has(index)) return false;
                    return true;
                });

                return {
                    tests: {
                        ...currentTests,
                        blocks: newBlocks,
                    },
                };
            });
        },
        [onUpdate],
    );

    const primaryColor = data.customizations?.colors?.primary || 'bg-brand-blue';
    const dividerConfig = data.customizations?.divider;

    const resolveLinkedTableBlock = (block: TestBlock): TableBlock | undefined => {
        if (block.block_type !== 'text') return undefined;
        const linkedId = (block as TextBlock).linked_table_id;
        if (!linkedId) return undefined;

        const sourceBlocks = testsSection.blocks || [];
        const found = sourceBlocks.find(
            (b) =>
                b.block_type === 'table' && stripSplitBlockId(b.id) === stripSplitBlockId(linkedId),
        );
        return found as TableBlock | undefined;
    };

    const handleConsolidateWithAI = async (tableBlock: TableBlock) => {
        // `aria-disabled` styles the button but does not stop a programmatic
        // click, and this is the single-flight lock's entry point.
        if (isGenerating) return;

        if (aiCreditAccess.shouldBlock) {
            toast.warning(getEntitlementBlockedMessage(aiCreditAccess.decision));
            return;
        }

        if (!data.id) {
            toast.error('Salve o laudo antes de consolidar com IA.');
            return;
        }

        const allBlocks = testsSection.blocks || [];
        const existingLinkedTextBlock = allBlocks.find(
            (b): b is TextBlock =>
                b.block_type === 'text' &&
                !!(b as TextBlock).linked_table_id &&
                stripSplitBlockId((b as TextBlock).linked_table_id) ===
                    stripSplitBlockId(tableBlock.id),
        );

        const linkedTextBlock = existingLinkedTextBlock || createTextBlock(tableBlock.id);
        if (!existingLinkedTextBlock) {
            setConsolidatingTableId(tableBlock.id || null);
        }
        const blockIdentifier = linkedTextBlock.id || linkedTextBlock.order;
        const normalizedBlockId = stripSplitBlockId(blockIdentifier);

        setIsGenerating(true);
        const generation = beginGeneration();
        setGeneratingTestBlockId(normalizedBlockId);
        setGeneratingTableId(tableBlock.id || null);
        // So the section panels can name what is holding them up. Safe for the
        // canvas: `ReportSection` excludes 'tests' from the skeleton it drives
        // off this id, and nothing else reads it.
        setGeneratingSectionId(SECTIONS.TESTS);
        await silentSave();

        try {
            const response = await aiApi.generate(
                data.id,
                'tests',
                {
                    cid_codes: data.selected_cids,
                    extra_details: data.diagnosis_details,
                    behaviors: data.selected_behaviors,
                    sessions_quantity: data.num_sessions,
                    behavior_observation_survey: data.behavior_observations_survey,
                    section_text: linkedTextBlock.content || '',
                    test_table_block: tableBlock,
                },
                'create',
                undefined,
                generation.options,
            );

            onUpdate((prev) => {
                const currentTests = prev.tests || { section_title: '', blocks: [] };
                const currentBlocks = currentTests.blocks || [];
                const blockAlreadyExists = currentBlocks.some(
                    (b) => b.id && stripSplitBlockId(b.id) === normalizedBlockId,
                );

                let newBlocks: TestBlock[];
                if (blockAlreadyExists) {
                    newBlocks = currentBlocks.map((b) => {
                        if (!b.id || stripSplitBlockId(b.id) !== normalizedBlockId) return b;
                        return {
                            ...b,
                            content: response.content,
                            has_content: true,
                            automatic: false,
                        };
                    });
                } else {
                    const tableIndex = currentBlocks.findIndex(
                        (b) => b.id && stripSplitBlockId(b.id) === stripSplitBlockId(tableBlock.id),
                    );
                    newBlocks = [...currentBlocks];
                    newBlocks.splice(tableIndex + 1, 0, {
                        ...linkedTextBlock,
                        content: response.content,
                        has_content: true,
                        automatic: false,
                    });
                }

                return { tests: { ...currentTests, blocks: newBlocks } };
            });
        } catch (error) {
            // The user stopped it. The instrument's text is untouched and no
            // credit was spent, and the button they pressed is answer enough.
            if (isAiGenerationCancelled(error)) return;

            console.error('Failed to consolidate test with AI', error);
            toast.error(
                error instanceof Error && error.message.trim()
                    ? error.message
                    : 'Erro ao consolidar instrumento com IA.',
            );
        } finally {
            setIsGenerating(false);
            setGeneratingTestBlockId(null);
            setGeneratingTableId(null);
            setGeneratingSectionId(null);
            setConsolidatingTableId(null);
            endGeneration();
        }
    };

    const isConsolidateBlocked = isGenerating || aiCreditAccess.shouldBlock;

    /**
     * The tools panel fires block actions it cannot run itself — AI
     * consolidation owns a single-flight lock and the skeleton state that lives
     * here. It hands the request over as `pendingBlockAction`; this consumes it
     * and clears it, the same way `pendingEditBlockId` works in ReportPreview.
     *
     * Only the live render may consume it: the hidden measurer mounts a second
     * copy of this component and would fire the action twice.
     */
    useEffect(() => {
        if (!pendingBlockAction || measureBlocks || disabled) return;

        const { realId, action } = pendingBlockAction;
        const target = (testsSection.blocks || []).find(
            (candidate) => stripSplitBlockId(getBlockRealId(candidate)) === realId,
        );

        setPendingBlockAction(null);
        if (!target) return;

        if (action === 'consolidate' && target.block_type === 'table') {
            if (isConsolidateBlocked) {
                toast.warning(getConsolidateTooltip(target.id));
                return;
            }
            handleConsolidateWithAI(target as TableBlock);
            return;
        }

        if (action === 'edit-chart' && target.block_type === 'chart') {
            onEditChart(target);
            return;
        }

        if (action === 'replace-image' && target.block_type === 'image') {
            // The picker is a hidden input owned by the rendered TestImage;
            // it exposes itself through this data attribute.
            const picker = document.querySelector<HTMLElement>(
                `[data-image-picker-for="${realId}"]`,
            );
            picker?.click();
        }
    }, [pendingBlockAction, measureBlocks, disabled]);

    /* Several of these buttons are on screen at once, so "why is this dead" is
       a different answer per table. The same-section case gets its own sentence:
       "a IA está gerando 'Análise'" is true but useless while you are standing
       in Análise looking at the instrument next to the one that is running. */
    const getConsolidateTooltip = (blockId?: string) => {
        if (generatingTableId && blockId === generatingTableId) return 'Interpretando…';
        if (aiCreditAccess.shouldBlock)
            return getEntitlementBlockedMessage(aiCreditAccess.decision);
        if (!isGenerating) return 'Interpretar com IA';
        if (generatingSectionId === SECTIONS.TESTS)
            return 'Aguarde: a IA está interpretando outro instrumento.';
        return buildAiBusyReason(getGeneratingSectionTitle(generatingSectionId, sidebarSteps));
    };

    const renderBlockActions = (block: TestBlock) => {
        if (disabled) return null;

        const isLinkedTextBlock =
            block.block_type === 'text' && !!(block as TextBlock).linked_table_id;
        const isTableBlock = block.block_type === 'table';
        const isChartBlock = block.block_type === 'chart';

        if (isTableBlock && !SHOW_TABLE_BLOCK_RAIL) return null;

        if (!isTableBlock && block.block_type === 'text') {
            const hasContent = !!(block as TextBlock).content?.trim();
            if (hasContent) return null;
        }

        // Every block type gates off the same group root now. The rail is a DOM
        // descendant of it (even for tables, where it is anchored to the inner
        // `group/table` for positioning), so hovering the rail itself keeps the
        // group alive without any keep-alive handlers of its own.
        const visibilityClass = 'group-data-[hover-intent=on]/block:opacity-100';

        return (
            <TooltipProvider delayDuration={0}>
                <div
                    data-block-selection-ui
                    className={`absolute -right-10 top-0 flex flex-col gap-1 opacity-0 ${visibilityClass} z-10 rounded-md border border-slate-200 bg-white p-1 shadow-md transition-all duration-200`}
                >
                    {isTableBlock && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    onClick={() => block.id && onAddChartFromTable(block.id)}
                                    className="flex size-8 items-center justify-center rounded text-slate-600 transition-colors hover:bg-slate-50 hover:text-blue-600"
                                >
                                    <Chart className="size-3.5" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="left">Adicionar gráfico</TooltipContent>
                        </Tooltip>
                    )}

                    {isTableBlock && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                {/* `aria-disabled`, never the native attribute:
                                    the latter brings `disabled:pointer-events-none`,
                                    so no hover ever reaches the trigger and the
                                    tooltip explaining the block cannot open. */}
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (isConsolidateBlocked) {
                                            toast.warning(getConsolidateTooltip(block.id));
                                            return;
                                        }
                                        handleConsolidateWithAI(block as TableBlock);
                                    }}
                                    aria-disabled={isConsolidateBlocked}
                                    className={`flex size-8 items-center justify-center rounded text-slate-600 transition-colors hover:bg-slate-50 hover:text-purple-600 ${
                                        isConsolidateBlocked ? 'cursor-not-allowed opacity-50' : ''
                                    }`}
                                >
                                    <Sparkles className="size-3.5" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="max-w-56 leading-5">
                                {getConsolidateTooltip(block.id)}
                            </TooltipContent>
                        </Tooltip>
                    )}

                    {isChartBlock && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    onClick={() => onEditChart(block)}
                                    className="flex size-8 items-center justify-center rounded text-slate-600 transition-colors hover:bg-slate-50 hover:text-blue-600"
                                >
                                    <Edit2 className="size-3.5" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="left">Editar gráfico</TooltipContent>
                        </Tooltip>
                    )}

                    {!isLinkedTextBlock && (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    onClick={() => handleDeleteBlock(block.id || block.order)}
                                    className="flex size-8 items-center justify-center rounded text-slate-600 transition-colors hover:bg-red-50 hover:text-red-600"
                                >
                                    <Trash className="size-3.5" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="left">Remover bloco</TooltipContent>
                        </Tooltip>
                    )}
                </div>
            </TooltipProvider>
        );
    };

    // Unique Droppable ID per page/chunk to avoid collisions and allow correct insertion index
    const droppableId = droppableKey
        ? `test_blocks_${resolvedBlockStartIndex}_${droppableKey}`
        : `test_blocks_${resolvedBlockStartIndex}`;

    // Whether the drag-in drop indicator should render before the block at this local index.
    // Gated on !measureBlocks so the hidden measurer never emits it (pagination stays untouched).
    const isDropTargetHere = (localIndex: number) =>
        !measureBlocks &&
        dropTarget?.droppableId === droppableId &&
        dropTarget.index === localIndex;

    if (testBlocks.length === 0) {
        if (!enableDragAndDrop) {
            return (
                <div
                    ref={sectionRef}
                    className="flex items-center justify-center rounded-xl border-2 border-dashed border-slate-200 text-slate-400"
                    style={{ minHeight: DEFAULT_SECTION_HEIGHT }}
                >
                    Arraste blocos aqui para adicionar
                </div>
            );
        }

        return (
            <Droppable droppableId={droppableId}>
                {(provided: DroppableProvided) => (
                    <div
                        ref={(node: HTMLDivElement | null) => {
                            provided.innerRef(node);
                            sectionRef.current = node;
                        }}
                        {...provided.droppableProps}
                        className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 text-slate-400"
                        style={{ minHeight: DEFAULT_SECTION_HEIGHT }}
                    >
                        {/* CTA never renders in the hidden measurer so the reserved
                            EMPTY_PLACEHOLDER_HEIGHT keeps matching what users see. */}
                        {!measureBlocks && !disabled ? (
                            hasPatient ? (
                                <>
                                    {/* The same two-item menu the panel card
                                        offers: starting a laudo from the canvas
                                        is how most people start one, and it was
                                        the half that never learned an
                                        instrument can be free. */}
                                    <AddInstrumentMenu
                                        surface="empty_state"
                                        align="center"
                                        onSelect={handleAddInstrumentFromCanvas}
                                    >
                                        <button
                                            type="button"
                                            data-block-selection-ui
                                            className="hover:bg-primary/92 inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-elevation-1 transition-colors"
                                        >
                                            + Adicionar instrumento
                                            <ArrowDown2 className="size-4" aria-hidden="true" />
                                        </button>
                                    </AddInstrumentMenu>
                                    <span className="text-xs">
                                        Comece pelo primeiro instrumento da bateria.
                                    </span>
                                </>
                            ) : (
                                /* Without a patient there is no report to put an
                                   instrument in, so the section asks for the one
                                   thing that unblocks it instead of offering a
                                   CTA that cannot work. */
                                <>
                                    <button
                                        type="button"
                                        data-block-selection-ui
                                        onClick={handleChoosePatientFromCanvas}
                                        className="rounded-lg border border-border bg-surface px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-muted"
                                    >
                                        Escolher paciente
                                    </button>
                                    <span className="text-xs">
                                        Escolha o paciente para liberar esta seção.
                                    </span>
                                </>
                            )
                        ) : (
                            'Arraste blocos aqui para adicionar'
                        )}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        );
    }

    if (!enableDragAndDrop) {
        return (
            <div
                ref={sectionRef}
                className={`space-y-5 ${BLOCK_TEXT_SPACING_RESET}`}
                style={{ minHeight: DEFAULT_TITLE_HEIGHT }}
            >
                {testBlocks.map((block, _index) => {
                    const uniqueKey = (block.id || block.order).toString();
                    const isEditing =
                        editingBlockId === uniqueKey ||
                        (typeof editingBlockId === 'string' &&
                            stripSplitBlockId(editingBlockId) === stripSplitBlockId(uniqueKey));
                    const linkedTableBlock = resolveLinkedTableBlock(block);

                    const Content = (
                        <div className="space-y-4">
                            {block.block_type === 'table' && (
                                <div className="group/table relative space-y-4">
                                    {!measureBlocks && renderBlockActions(block)}
                                    <TestTable
                                        block={block}
                                        data={data}
                                        primaryColor={primaryColor}
                                        onUpdate={onUpdate}
                                        onBlockUpdate={handleBlockUpdate}
                                        onAddTest={onAddTest}
                                        onAddChartFromTable={onAddChartFromTable}
                                        disabled={disabled}
                                        measurementId={
                                            measureBlocks
                                                ? getBlockMeasurementId(uniqueKey)
                                                : undefined
                                        }
                                        footnotes={
                                            footnotePlan?.byBlockId[getFootnoteBlockId(block)]
                                        }
                                    />
                                    <AISectionSkeleton
                                        visible={consolidatingTableId === block.id}
                                        sectionId="tests"
                                    />
                                </div>
                            )}

                            {block.block_type === 'chart' && (
                                <TestChart
                                    block={block}
                                    data={data}
                                    disabled={disabled}
                                    onEditChart={onEditChart}
                                    onBlockUpdate={handleBlockUpdate}
                                    isMeasuring={measureBlocks}
                                />
                            )}

                            {block.block_type === 'image' && (
                                <div className="group/image-block relative">
                                    {!measureBlocks && renderBlockActions(block)}
                                    <TestImage
                                        block={block}
                                        disabled={disabled}
                                        onBlockUpdate={handleBlockUpdate}
                                        isMeasuring={measureBlocks}
                                    />
                                </div>
                            )}

                            {block.block_type === 'text' && (
                                <TestText
                                    block={block}
                                    onBlockUpdate={handleBlockUpdate}
                                    onDelete={() => handleDeleteBlock(block.id || block.order)}
                                    isEditing={isEditing}
                                    onEditingChange={(editing: boolean) =>
                                        onEditingBlockChange?.(editing ? uniqueKey : null)
                                    }
                                    isAfterTable={Boolean(linkedTableBlock)}
                                    previousTableBlock={linkedTableBlock}
                                    reportData={data}
                                />
                            )}

                            {block.block_type === 'divider' && (
                                <TestDivider
                                    block={block}
                                    primaryColor={primaryColor}
                                    dividerConfig={dividerConfig}
                                    onBlockUpdate={handleBlockUpdate}
                                    disabled={disabled}
                                />
                            )}

                            {block.block_type === 'divider' && block.description && (
                                <p className="mt-1 text-sm text-slate-500">
                                    <EditableText
                                        value={block.description}
                                        onSave={(value) =>
                                            handleBlockUpdate(block.id || block.order, {
                                                description: value,
                                            })
                                        }
                                        placeholder="Descrição da seção..."
                                        className="inline"
                                        disabled={disabled}
                                    />
                                </p>
                            )}
                        </div>
                    );

                    const realId = getBlockRealId(block);
                    // A split fragment at the end of a page chunk continues on
                    // the next page — a paste target there would sit mid-block.
                    const suppressAfterGap =
                        isSplitBlockId(block.id) && _index === testBlocks.length - 1;
                    const scopeBand = resolveScopeBand(_index);

                    return (
                        <HoverIntentGroup
                            key={uniqueKey}
                            hoverIntent={{ disabled: disabled || measureBlocks }}
                            // `isolate` only while banded: the band paints at
                            // -z-10 and would otherwise fall behind the page
                            // paper instead of behind this block's content.
                            className={`group/block relative ${scopeBand ? 'isolate' : ''} ${selectionRingClass(realId)}`}
                            data-preview-block-id={measureBlocks ? undefined : uniqueKey}
                            data-scope-highlighted={highlightedIds.has(realId) || undefined}
                            {...selectionCaptureProps(realId)}
                        >
                            {scopeBand && <ScopeBand {...scopeBand} />}
                            {showSelectionChrome &&
                                resolvedBlockStartIndex === 0 &&
                                _index === 0 && <BlockPasteGap realId={realId} position="before" />}
                            {showSelectionChrome && !suppressAfterGap && (
                                <BlockPasteGap realId={realId} position="after" />
                            )}
                            {!measureBlocks &&
                                block.block_type !== 'table' &&
                                renderBlockActions(block)}
                            {measureBlocks ? (
                                <MeasureWrapper id={`block-${uniqueKey}`}>{Content}</MeasureWrapper>
                            ) : (
                                Content
                            )}
                        </HoverIntentGroup>
                    );
                })}
            </div>
        );
    }

    return (
        <Droppable droppableId={droppableId} isDropDisabled={disabled}>
            {(provided: DroppableProvided) => (
                <div
                    ref={(node: HTMLDivElement | null) => {
                        provided.innerRef(node);
                        sectionRef.current = node;
                    }}
                    {...provided.droppableProps}
                    className={`space-y-5 ${BLOCK_TEXT_SPACING_RESET}`}
                    style={{ minHeight: DEFAULT_TITLE_HEIGHT }}
                >
                    {testBlocks.map((block, index) => {
                        const uniqueKey = (block.id || block.order).toString();
                        const draggableKey = block.id ? block.id : `order-${block.order}`;
                        const isEditing =
                            editingBlockId === uniqueKey ||
                            (typeof editingBlockId === 'string' &&
                                stripSplitBlockId(editingBlockId) === stripSplitBlockId(uniqueKey));
                        const linkedTableBlock = resolveLinkedTableBlock(block);

                        const Content = (
                            <div className="space-y-4">
                                {block.block_type === 'table' && (
                                    <div className="group/table relative space-y-4">
                                        {renderBlockActions(block)}
                                        <TestTable
                                            block={block}
                                            data={data}
                                            primaryColor={primaryColor}
                                            onUpdate={onUpdate}
                                            onBlockUpdate={handleBlockUpdate}
                                            onAddTest={onAddTest}
                                            onAddChartFromTable={onAddChartFromTable}
                                            disabled={disabled}
                                            measurementId={
                                                measureBlocks
                                                    ? getBlockMeasurementId(uniqueKey)
                                                    : undefined
                                            }
                                            footnotes={
                                                footnotePlan?.byBlockId[getFootnoteBlockId(block)]
                                            }
                                        />
                                        <AISectionSkeleton
                                            visible={consolidatingTableId === block.id}
                                            sectionId="tests"
                                        />
                                    </div>
                                )}

                                {block.block_type === 'chart' && (
                                    <TestChart
                                        block={block}
                                        data={data}
                                        disabled={disabled}
                                        onEditChart={onEditChart}
                                        onBlockUpdate={handleBlockUpdate}
                                        isMeasuring={measureBlocks}
                                    />
                                )}

                                {block.block_type === 'image' && (
                                    <div className="group/image-block relative">
                                        {renderBlockActions(block)}
                                        <TestImage
                                            block={block}
                                            disabled={disabled}
                                            onBlockUpdate={handleBlockUpdate}
                                            isMeasuring={measureBlocks}
                                        />
                                    </div>
                                )}

                                {block.block_type === 'text' && (
                                    <TestText
                                        block={block}
                                        onBlockUpdate={handleBlockUpdate}
                                        onDelete={() => handleDeleteBlock(block.id || block.order)}
                                        isEditing={isEditing}
                                        onEditingChange={(editing: boolean) =>
                                            onEditingBlockChange?.(editing ? uniqueKey : null)
                                        }
                                        isAfterTable={Boolean(linkedTableBlock)}
                                        previousTableBlock={linkedTableBlock}
                                        reportData={data}
                                    />
                                )}

                                {block.block_type === 'divider' && (
                                    <TestDivider
                                        block={block}
                                        primaryColor={primaryColor}
                                        dividerConfig={dividerConfig}
                                        onBlockUpdate={handleBlockUpdate}
                                        disabled={disabled}
                                    />
                                )}

                                {block.block_type === 'divider' && block.description && (
                                    <p className="mt-1 text-sm text-slate-500">
                                        <EditableText
                                            value={block.description}
                                            onSave={(value) =>
                                                handleBlockUpdate(block.id || block.order, {
                                                    description: value,
                                                })
                                            }
                                            placeholder="Descrição da seção..."
                                            className="inline"
                                            disabled={disabled}
                                        />
                                    </p>
                                )}
                            </div>
                        );

                        const realId = getBlockRealId(block);
                        const suppressAfterGap =
                            isSplitBlockId(block.id) && index === testBlocks.length - 1;
                        // A continuation fragment at the top of a chunk continues from the
                        // previous page — a drop above it would land mid-block, so hide the
                        // leading "before" drop indicator.
                        const suppressBeforeIndicator =
                            index === 0 && isSplitContinuationId(block.id);

                        // Continuation fragments continue a block that starts on the
                        // previous page — dragging them would be ambiguous, so only
                        // the head fragment (or a whole block) is draggable. The
                        // drop is resolved by real id, never by dnd's indices.
                        const canDragBlock =
                            !disabled && !measureBlocks && !isSplitContinuationId(block.id);
                        const scopeBand = resolveScopeBand(index);

                        return (
                            <Draggable
                                key={uniqueKey}
                                draggableId={draggableKey}
                                index={index}
                                isDragDisabled={!canDragBlock}
                            >
                                {(provided: DraggableProvided, snapshot) => (
                                    <HoverIntentGroup
                                        innerRef={provided.innerRef}
                                        hoverIntent={{ disabled: disabled || measureBlocks }}
                                        {...provided.draggableProps}
                                        style={provided.draggableProps.style}
                                        // `isolate` only while banded: the band
                                        // paints at -z-10 and would otherwise fall
                                        // behind the page paper instead of behind
                                        // this block's content.
                                        // While in flight the block is the
                                        // thing the user is deciding about, so
                                        // it reads as lifted off the paper —
                                        // solid, shadowed, ringed. It used to
                                        // fade to 50%, which dimmed the object
                                        // under the cursor and left nothing to
                                        // aim with; dnd keeps this element
                                        // position:fixed, so the fade was on
                                        // the moving clone, not on a residue at
                                        // the origin.
                                        className={`group/block relative transition-colors ${scopeBand ? 'isolate' : ''} ${selectionRingClass(realId)} ${
                                            snapshot.isDragging
                                                ? 'rounded-md bg-white shadow-xl ring-1 ring-blue-400/70'
                                                : ''
                                        }`}
                                        data-preview-block-id={
                                            measureBlocks ? undefined : uniqueKey
                                        }
                                        data-scope-highlighted={
                                            highlightedIds.has(realId) || undefined
                                        }
                                        {...selectionCaptureProps(realId)}
                                    >
                                        {scopeBand && <ScopeBand {...scopeBand} />}
                                        {canDragBlock && (
                                            // A local provider: the rail's provider
                                            // (delay 0) wraps only the block actions,
                                            // and a grip that pops the instant the
                                            // pointer drifts past it would be noise.
                                            <TooltipProvider delayDuration={400}>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        {/* `dragHandleProps` stays spread
                                                        first: Radix merges its own props
                                                        onto the child, so dnd keeps its
                                                        handlers and its `aria-describedby`
                                                        lift instructions. */}
                                                        <button
                                                            type="button"
                                                            {...provided.dragHandleProps}
                                                            data-block-selection-ui
                                                            aria-label="Mover bloco"
                                                            className="absolute -left-8 top-1 z-10 flex h-8 w-6 cursor-grab items-center justify-center rounded-md text-slate-300 opacity-0 transition-opacity hover:bg-slate-100 hover:text-slate-500 focus-visible:opacity-100 active:cursor-grabbing group-data-[hover-intent=on]/block:opacity-100"
                                                        >
                                                            <GripVertical className="size-4" />
                                                        </button>
                                                    </TooltipTrigger>
                                                    <TooltipContent side="left">
                                                        Arraste para mover o bloco
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        )}
                                        {showSelectionChrome &&
                                            resolvedBlockStartIndex === 0 &&
                                            index === 0 && (
                                                <BlockPasteGap realId={realId} position="before" />
                                            )}
                                        {showSelectionChrome && !suppressAfterGap && (
                                            <BlockPasteGap realId={realId} position="after" />
                                        )}

                                        {!suppressBeforeIndicator && isDropTargetHere(index) && (
                                            <TestDropIndicator
                                                position="before"
                                                height={dropTarget!.height}
                                                pending={dropTarget!.pending}
                                            />
                                        )}
                                        {index === testBlocks.length - 1 &&
                                            !suppressAfterGap &&
                                            isDropTargetHere(testBlocks.length) && (
                                                <TestDropIndicator
                                                    position="after"
                                                    height={dropTarget!.height}
                                                    pending={dropTarget!.pending}
                                                />
                                            )}

                                        {block.block_type !== 'table' && renderBlockActions(block)}

                                        {measureBlocks ? (
                                            <MeasureWrapper id={`block-${uniqueKey}`}>
                                                {Content}
                                            </MeasureWrapper>
                                        ) : (
                                            Content
                                        )}
                                    </HoverIntentGroup>
                                )}
                            </Draggable>
                        );
                    })}
                    {provided.placeholder}
                </div>
            )}
        </Droppable>
    );
}
